import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatDate, formatTRY } from "@/lib/api";
import { Printer, ArrowLeft } from "lucide-react";

export default function QuotePrint() {
  const { id } = useParams();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/quotes/${id}`).then((r) => setQ(r.data)).catch(() => setErr("Teklif bulunamadı"));
  }, [id]);

  useEffect(() => {
    if (q) setTimeout(() => window.print(), 400);
  }, [q]);

  if (err) return <div className="p-8 text-center text-rose-600">{err}</div>;
  if (!q) return <div className="p-8 text-center text-slate-500">Yükleniyor…</div>;

  return (
    <div className="bg-white min-h-screen">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
        }
        @page { size: A4; margin: 15mm; }
      `}</style>

      <div className="no-print bg-slate-900 text-white px-6 py-3 flex items-center justify-between sticky top-0 z-50">
        <a href="/quotes" className="inline-flex items-center gap-2 text-sm hover:text-blue-300" data-testid="print-back">
          <ArrowLeft className="w-4 h-4" /> Geri
        </a>
        <button
          data-testid="print-btn"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-md text-sm font-medium"
        >
          <Printer className="w-4 h-4" /> Yazdır
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-8 sm:p-12 text-slate-900" data-testid="quote-print-doc">
        {/* Header */}
        <div className="flex items-start justify-between border-b-2 border-slate-900 pb-6 mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold">Global Car Service</h1>
            <p className="text-sm text-slate-600 mt-1">Kaporta · Boya · Şase Onarım</p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Teklif / İş Emri</p>
            <p className="font-display text-2xl font-bold font-mono-num mt-1">{q.quote_number}</p>
            <p className="text-xs text-slate-600 mt-1">Tarih: {formatDate(q.date)}</p>
            {q.valid_until && <p className="text-xs text-slate-600">Geçerlilik: {formatDate(q.valid_until)}</p>}
          </div>
        </div>

        {/* Customer */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2">Müşteri</p>
            <p className="font-semibold text-lg">{q.customer_name}</p>
            {q.customer_phone && <p className="text-sm text-slate-600">Tel: {q.customer_phone}</p>}
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2">Araç</p>
            {q.plate && (
              <p className="inline-block px-3 py-1 bg-slate-900 text-white rounded font-mono-num font-bold text-lg">
                {q.plate}
              </p>
            )}
            {q.car_model && <p className="text-sm text-slate-600 mt-1">{q.car_model}</p>}
          </div>
        </div>

        {/* Items */}
        <table className="w-full mb-6 border-collapse">
          <thead>
            <tr className="bg-slate-100 border-t-2 border-b-2 border-slate-900">
              <th className="text-left px-3 py-2 text-xs uppercase font-bold w-10">#</th>
              <th className="text-left px-3 py-2 text-xs uppercase font-bold">Açıklama</th>
              <th className="text-right px-3 py-2 text-xs uppercase font-bold w-20">Adet</th>
              <th className="text-right px-3 py-2 text-xs uppercase font-bold w-32">Birim ₺</th>
              <th className="text-right px-3 py-2 text-xs uppercase font-bold w-36">Tutar ₺</th>
            </tr>
          </thead>
          <tbody>
            {q.items.map((it, i) => (
              <tr key={`${i}-${it.description || "row"}`} className="border-b border-slate-200">
                <td className="px-3 py-2 text-sm font-mono-num text-slate-600">{i + 1}</td>
                <td className="px-3 py-2 text-sm">{it.description}</td>
                <td className="px-3 py-2 text-sm text-right font-mono-num">{it.quantity}</td>
                <td className="px-3 py-2 text-sm text-right font-mono-num">{formatTRY(it.unit_price)}</td>
                <td className="px-3 py-2 text-sm text-right font-mono-num font-semibold">
                  {formatTRY((it.quantity || 0) * (it.unit_price || 0))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end mb-8">
          <div className="w-80 space-y-2 text-sm font-mono-num">
            <div className="flex justify-between py-1">
              <span className="text-slate-600">Ara Toplam</span>
              <span className="font-semibold">{formatTRY(q.subtotal)}</span>
            </div>
            {q.discount_amount > 0 && (
              <div className="flex justify-between py-1 text-rose-700">
                <span>İskonto {q.discount_type === "percent" ? `(%${q.discount_value})` : ""}</span>
                <span>-{formatTRY(q.discount_amount)}</span>
              </div>
            )}
            <div className="flex justify-between py-1">
              <span className="text-slate-600">KDV Matrahı</span>
              <span>{formatTRY(q.taxable_amount)}</span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-slate-600">KDV ({(q.vat_rate * 100).toFixed(0)}%)</span>
              <span>{formatTRY(q.vat_amount)}</span>
            </div>
            <div className="flex justify-between py-3 border-t-2 border-slate-900 text-lg font-bold">
              <span>Genel Toplam</span>
              <span>{formatTRY(q.total)}</span>
            </div>
          </div>
        </div>

        {q.notes && (
          <div className="border-t border-slate-200 pt-4 mb-8">
            <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-1">Notlar</p>
            <p className="text-sm text-slate-700">{q.notes}</p>
          </div>
        )}

        {/* Signatures */}
        <div className="grid grid-cols-2 gap-16 mt-16 pt-8">
          <div className="text-center border-t-2 border-slate-400 pt-2">
            <p className="text-xs text-slate-600">Müşteri İmza</p>
          </div>
          <div className="text-center border-t-2 border-slate-400 pt-2">
            <p className="text-xs text-slate-600">Yetkili İmza · Kaşe</p>
          </div>
        </div>

        <p className="text-center text-[10px] text-slate-500 mt-12">
          Bu belge bilgi amaçlıdır. Resmi fatura değildir.
        </p>
      </div>
    </div>
  );
}
